import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicLayout from "@/components/PublicLayout";
import { useAuth } from "@/context/AuthContext";
import { primaryHomeForRoles } from "@/lib/auth";
import {
  ArrowRight, Check, Play, Building2, Handshake, Sparkles, Cpu, Radio,
  ShieldCheck, Zap, Star, Users, Cog, Award, GraduationCap, LineChart,
  Globe, KeyRound, Layers, TrendingUp,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Fase 14.6 · Landing repositioning B2B / B2B2C                      */
/* ------------------------------------------------------------------ */

const B2B_STEPS = [
  { title: "Agenda demo", desc: "Diseñamos el paquete de licencias según tu equipo y objetivos regulatorios." },
  { title: "Configuramos tenant", desc: "Rebranding, roles, cohortes y catálogo curado para tu institución." },
  { title: "Onboarding masivo", desc: "Cargas tu equipo por CSV o via API. Notificaciones automatizadas." },
  { title: "Medes con reportería", desc: "MRR de conocimiento, cohort analysis, KPIs de completación y NPS." },
];
const PARTNER_STEPS = [
  { title: "Firmamos alianza", desc: "Acuerdo de marca compartida y modelo de comisiones (default 50/50)." },
  { title: "Configuras marca", desc: "Landing white-label, dominio, catálogo y branding editable sin código." },
  { title: "Vendes bajo tu dominio", desc: "Toda la infraestructura DCC opera bajo tu nombre y tu look & feel." },
  { title: "Cobras comisiones", desc: "Statements mensuales con PDF y pagos automatizados (SPEI/Stripe mock)." },
];

const ECOSYSTEM = [
  { icon: GraduationCap, title: "Contenido curado por expertos", desc: "195+ programas actualizados en banca, riesgo, wealth, fintech y ML." },
  { icon: Cpu, title: "Content Studio con IA", desc: "Crea programas propios en horas: video, audio, PDFs y slides." },
  { icon: Radio, title: "Salas en vivo Jitsi", desc: "Cohortes ilimitadas con instructores + grabación automática." },
  { icon: ShieldCheck, title: "Certificados verificables", desc: "Folio público auditable. Emisión unlimited para tenants." },
  { icon: Layers, title: "Multi-tenant white-label", desc: "Branding, subdominio y catálogo por partner sin código." },
  { icon: KeyRound, title: "Public API v1 + Webhooks", desc: "Integra DCC con tu ERP, CRM, LMS o data warehouse." },
  { icon: LineChart, title: "Reportería ejecutiva", desc: "MRR, ARR, cohorts, LTV, funnel y attribution en dashboards." },
  { icon: TrendingUp, title: "Programa de afiliados", desc: "Comisiones y statements para universidades, asociaciones y creators." },
];

const USE_CASES = [
  {
    tag: "Banco comunitario",
    title: "Sofipo con 400 asesores",
    desc: "Certificación regulatoria anual + refresh continuo. DCC provee currícula compliance + tracking cross-branch.",
  },
  {
    tag: "Casa de bolsa",
    title: "120 promotores en HPCM y wealth",
    desc: "Certificación previa al cierre de trimestre. Cohortes semanales con senior instructor + evaluaciones.",
  },
  {
    tag: "Universidad de negocios",
    title: "Diplomado fintech bajo marca propia",
    desc: "Facultad de finanzas ofrece capacitación con branding de la escuela y 50% de revenue share.",
  },
  {
    tag: "Asociación gremial",
    title: "MOOC compliance a 8,000 afiliados",
    desc: "Colegio de contadores ofrece programas de cumplimiento con acceso masivo vía SSO SAML.",
  },
];

const SCREENSHOTS = [
  { src: "/marketing/catalog-public.png", title: "Catálogo público", desc: "195+ programas con filtros por área, formato y modalidad." },
  { src: "/marketing/alumno-lms.png", title: "LMS del alumno", desc: "Módulos, lecciones, salas en vivo y foros extendidos." },
  { src: "/marketing/content-studio.png", title: "Content Studio con IA", desc: "Genera programas completos desde brief editorial." },
  { src: "/marketing/superadmin-subs.png", title: "Reportería ejecutiva", desc: "MRR, ARR, cohorts y funnel de suscripciones." },
];

const PARTNERS = [
  "Banco Alfa", "Trafalgar Sofipo", "Trafalgar Casa de Bolsa", "Demo Bank",
  "Capital Training Institute", "Instituto LATAM de Finanzas",
  "Colegio Nacional de Compliance", "Riesgo Analytics",
];

const TESTIMONIALS = [
  {
    quote: "En 6 meses migramos toda nuestra capacitación regulatoria a DCC. El equipo de compliance pasó de armar contenido manual a curar programas listos y medir con métricas ejecutivas.",
    name: "Ana Rivera", role: "Directora de Cumplimiento", org: "Banco privado (LATAM)",
    initials: "AR", color: "#DC5A0F",
  },
  {
    quote: "Como universidad, nos permitió lanzar un diplomado en fintech con nuestra marca y nuestro dominio sin construir un LMS desde cero. La integración con nuestro CRM fue directa vía la API pública.",
    name: "Dr. Miguel Solís", role: "Director de Educación Continua", org: "Universidad privada",
    initials: "MS", color: "#F26A21",
  },
  {
    quote: "El módulo de afiliados nos abrió una línea nueva de negocio. Referimos alumnos con nuestro código y recibimos comisiones mensuales con statement PDF.",
    name: "Sofía Domínguez", role: "Founder", org: "Consultoría fintech",
    initials: "SD", color: "#0EA5E9",
  },
];

export default function Home() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [flow, setFlow] = useState("b2b"); // b2b | partner

  useEffect(() => {
    if (user) {
      const home = primaryHomeForRoles(user.roles);
      if (home && home !== "/") nav(home, { replace: true });
    }
  }, [user, nav]);

  const steps = flow === "b2b" ? B2B_STEPS : PARTNER_STEPS;

  return (
    <PublicLayout>
      <Helmet>
        <title>DCC · Plataforma de capacitación financiera para instituciones y aliados</title>
        <meta name="description" content="La plataforma B2B y white-label para capacitar equipos financieros y lanzar academias bajo tu marca. 195+ programas certificados, IA, salas en vivo, API pública." />
        <meta property="og:title" content="DCC · Plataforma de capacitación financiera B2B" />
        <meta property="og:description" content="La plataforma B2B y white-label para capacitar equipos financieros y lanzar academias bajo tu marca." />
        <meta property="og:image" content="/marketing/catalog-public.png" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* 1 · Hero ---------------------------------------------------- */}
      <section className="relative bg-white">
        <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(ellipse_at_top,#FFEDD5,transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-primary bg-primary/10 border border-primary/30 rounded-full px-3 py-1"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                DCC · Debt Capital College
              </span>
              <h1
                data-testid="hero-title"
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.05] font-display"
              >
                La plataforma de capacitación financiera <br />
                <span className="text-primary">para instituciones y aliados</span>
              </h1>
              <p
                data-testid="hero-subtitle"
                className="mt-6 max-w-xl text-lg text-slate-600 leading-relaxed"
              >
                Forma equipos de finanzas de élite, activa programas de educación bajo tu
                marca white-label o integra nuestro contenido en tu ecosistema. 195+ programas
                certificados, IA generativa y reportería ejecutiva en un solo stack.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/demo"
                  data-testid="hero-cta-demo"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md bg-primary hover:brightness-110 text-white font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/30"
                >
                  Agenda una demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/catalogo"
                  data-testid="hero-cta-explore"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-md border border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold text-sm transition-all"
                >
                  Explora la plataforma
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-slate-500" data-testid="hero-chips">
                <Chip>Multi-tenant</Chip>
                <span className="text-slate-300">·</span>
                <Chip>White-label</Chip>
                <span className="text-slate-300">·</span>
                <Chip>Public API</Chip>
                <span className="text-slate-300">·</span>
                <Chip>Certificados verificables</Chip>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-200/60 bg-white">
                <img
                  src="/marketing/catalog-public.png"
                  alt="Portal DCC · Catálogo público"
                  className="w-full h-auto block"
                  data-testid="hero-visual"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 backdrop-blur text-[10px] font-bold uppercase tracking-widest text-primary border border-primary/30">
                  <Play className="w-2.5 h-2.5" /> Vista del portal
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · Dos formas de trabajar con DCC -------------------------- */}
      <Section eyebrow="Dos modelos, una infraestructura"
                title="Dos formas de trabajar con DCC" bg="slate">
        <div className="grid md:grid-cols-2 gap-6" data-testid="dual-track-grid">
          <TrackCard
            testid="track-b2b"
            icon={Building2}
            label="Para instituciones financieras"
            title="Capacita a tu equipo con currícula acreditada"
            bullets={[
              "Licencias por asientos con onboarding masivo (CSV o API)",
              "195+ programas actualizados: HPCM, riesgo, compliance, wealth, fintech, ML aplicada",
              "Reportería ejecutiva con MRR de conocimiento, cohort analysis y KPIs de completación",
              "Salas en vivo con instructores + certificados verificables por auditores",
            ]}
            ctaTo="/demo?type=enterprise"
            ctaLabel="Agenda una demo para tu institución"
            ctaTestid="track-b2b-cta"
            linkTo="/para-instituciones"
            linkLabel="Ver detalles B2B"
          />
          <TrackCard
            testid="track-b2b2c"
            icon={Handshake}
            label="Para aliados white-label"
            title="Lanza tu propia academia de finanzas en semanas"
            bullets={[
              "Landing white-label editable sin código con tu marca y dominio",
              "Comisiones automatizadas + statements mensuales con PDF",
              "API pública + webhooks para integrar con tu ERP/CRM/LMS",
              "Contenido curado o crea el tuyo con nuestro Content Studio de IA",
            ]}
            ctaTo="/demo?type=partner"
            ctaLabel="Solicita alianza white-label"
            ctaTestid="track-b2b2c-cta"
            linkTo="/para-aliados"
            linkLabel="Ver detalles aliados"
          />
        </div>
      </Section>

      {/* 3 · Cómo funciona (tabs B2B / B2B2C) ------------------------ */}
      <Section eyebrow="Cómo funciona"
                title={flow === "b2b" ? "De la primera demo al primer cohort" : "De la primera reunión al primer statement"}>
        <div className="flex justify-center gap-1 mb-8" data-testid="how-tabs">
          <TabToggle active={flow === "b2b"} onClick={() => setFlow("b2b")}
                      testid="how-tab-b2b">B2B (institución)</TabToggle>
          <TabToggle active={flow === "partner"} onClick={() => setFlow("partner")}
                      testid="how-tab-partner">B2B2C (aliado white-label)</TabToggle>
        </div>
        <div className="grid md:grid-cols-4 gap-5" data-testid="how-steps-grid">
          {steps.map((s, i) => (
            <div key={i} className="p-6 bg-white border border-slate-200 rounded-xl hover:border-primary/40 transition-colors">
              <div className="w-9 h-9 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center mb-4 font-mono text-primary text-sm font-bold">
                0{i + 1}
              </div>
              <h3 className="text-base font-bold text-slate-900 leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 4 · Ecosistema --------------------------------------------- */}
      <Section eyebrow="Ecosistema"
                title="Todo lo que necesitas en un solo stack"
                bg="slate">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ecosystem-grid">
          {ECOSYSTEM.map((t, i) => (
            <div key={i} className="p-5 bg-white border border-slate-200 rounded-xl">
              <t.icon className="w-5 h-5 text-primary" />
              <p className="mt-3 text-sm font-semibold text-slate-900 leading-snug">{t.title}</p>
              <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 5 · Casos de uso ------------------------------------------- */}
      <Section eyebrow="Casos de uso" title="Cómo lo usan nuestros clientes hoy">
        <div className="grid sm:grid-cols-2 gap-5" data-testid="case-uses-grid">
          {USE_CASES.map((c, i) => (
            <div key={i} className="p-6 bg-white border border-slate-200 rounded-xl">
              <p className="text-[10px] uppercase tracking-widest text-primary font-bold">{c.tag}</p>
              <h3 className="mt-2 text-lg font-bold text-slate-900 leading-snug">{c.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Casos representativos · datos anonimizados durante fase beta
        </p>
      </Section>

      {/* 6 · Screenshots del portal --------------------------------- */}
      <Section eyebrow="El portal en acción"
                title="Un vistazo real a la plataforma" bg="slate">
        <div className="grid sm:grid-cols-2 gap-5" data-testid="portal-screenshots">
          {SCREENSHOTS.map((s, i) => (
            <figure key={i} className="group">
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white shadow-md">
                <img
                  src={s.src}
                  alt={s.title}
                  className="w-full h-auto block group-hover:scale-[1.01] transition-transform duration-500"
                  onError={(e) => {
                    e.currentTarget.parentElement.innerHTML =
                      '<div class="aspect-video flex items-center justify-center bg-slate-100 text-slate-400 text-sm">Screenshot pendiente</div>';
                  }}
                />
              </div>
              <figcaption className="mt-3">
                <p className="text-sm font-semibold text-slate-900">{s.title}</p>
                <p className="text-xs text-slate-500">{s.desc}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </Section>

      {/* 7 · Instituciones que confían en nosotros ------------------ */}
      <Section eyebrow="Confían en DCC"
                title="Instituciones que ya usan nuestra plataforma">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="partners-grid-b2b">
          {PARTNERS.map((n) => (
            <div
              key={n}
              data-testid={`partner-${n.toLowerCase().replace(/\s+/g, "-")}`}
              className="h-20 flex items-center justify-center bg-slate-100 border border-slate-200 rounded-lg px-4"
            >
              <span className="font-display font-semibold text-slate-500 tracking-wide text-sm text-center grayscale">
                {n}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-center text-xs text-slate-400">
          Nombres representativos · integraciones piloto durante fase beta
        </p>
      </Section>

      {/* 8 · Testimonios ---------------------------------------------*/}
      <Section eyebrow="Voces de la comunidad"
                title="Lo que dicen las áreas de talento y educación" bg="slate">
        <div className="grid md:grid-cols-3 gap-5" data-testid="testimonials-mock">
          {TESTIMONIALS.map((t, i) => (
            <blockquote key={i} className="p-6 bg-white border border-slate-200 rounded-xl relative">
              <div className="absolute top-3 right-3 flex gap-0.5">
                {[0, 1, 2, 3, 4].map((n) => (
                  <Star key={n} className="w-3 h-3 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-sm text-slate-800 leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-5 flex items-center gap-3">
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm shadow"
                  style={{ backgroundColor: t.color }}
                >
                  {t.initials}
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">{t.role}</p>
                  <p className="text-[11px] text-slate-400">{t.org}</p>
                </div>
              </div>
            </blockquote>
          ))}
        </div>
        <p className="mt-4 text-center text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
          Testimonios mock · placeholders durante fase beta
        </p>
      </Section>

      {/* 9 · CTA final ---------------------------------------------- */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-orange-700 via-primary to-brand-orange-900 p-10 md:p-16 text-white text-center">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="relative max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] font-bold uppercase tracking-widest">
              <Zap className="w-3 h-3" /> Sin compromiso · 30 minutos
            </span>
            <h2 className="mt-5 text-4xl md:text-5xl font-extrabold font-display leading-tight">
              ¿Listo para llevar tu capacitación financiera al siguiente nivel?
            </h2>
            <p className="mt-4 text-lg text-white/80 leading-relaxed">
              Agenda una llamada de 30 minutos con nuestro equipo comercial.
            </p>
            <div className="mt-8">
              <Link
                to="/demo"
                data-testid="final-cta-demo"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-md bg-white text-primary font-bold text-base hover:bg-brand-orange-50 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-2xl"
              >
                Agendar demo <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-componentes                                                    */
/* ------------------------------------------------------------------ */

function Section({ eyebrow, title, children, bg }) {
  const bgClass = bg === "slate" ? "bg-slate-50" : "bg-white";
  return (
    <section className={`${bgClass} border-t border-slate-100`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs uppercase tracking-widest text-primary font-bold">{eyebrow}</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold text-slate-900 font-display leading-tight">
            {title}
          </h2>
        </div>
        {children}
      </div>
    </section>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Check className="w-3.5 h-3.5 text-emerald-600" />
      <span className="font-medium">{children}</span>
    </span>
  );
}

function TabToggle({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 rounded-md text-sm font-semibold border transition-colors ${
        active
          ? "bg-primary border-primary text-white"
          : "bg-white border-slate-300 text-slate-600 hover:border-primary hover:text-primary"
      }`}
    >
      {children}
    </button>
  );
}

function TrackCard({ icon: Icon, label, title, bullets, ctaTo, ctaLabel,
                     ctaTestid, linkTo, linkLabel, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-8 bg-white border-2 border-slate-200 rounded-2xl hover:border-primary/60 hover:shadow-xl hover:shadow-primary/5 transition-all flex flex-col"
    >
      <div className="flex items-start gap-4 mb-2">
        <div className="w-12 h-12 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-widest text-primary font-bold">{label}</p>
          <h3 className="mt-1 text-xl font-bold text-slate-900 leading-tight">{title}</h3>
        </div>
      </div>
      <ul className="mt-5 space-y-2.5 flex-1">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <div className="mt-6 flex flex-col sm:flex-row gap-2">
        <Link
          to={ctaTo}
          data-testid={ctaTestid}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary hover:brightness-110 text-white font-semibold text-sm transition-colors"
        >
          {ctaLabel} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to={linkTo}
          className="inline-flex items-center justify-center gap-1 px-4 py-3 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 font-semibold text-sm transition-colors"
        >
          {linkLabel}
        </Link>
      </div>
    </div>
  );
}
